import { useCallback, useMemo, useState } from "react";
import { Flex, Text, Badge } from "@radix-ui/themes";
import { Pencil1Icon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { RowActions, SimpleTable } from "@/v2/components/table/SimpleTable";
import {
  SnippetResource,
  ReleaseAccessStatus,
  ReleaseAccessResourceTopic,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.model";
import {
  useGetSnippetsByRelease,
  useUpdateSnippetAccess,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.api";
import { useGetTopics } from "@/api/useGetTopics";
import { AccessBadge } from "../components/AccessBadge";
import { ManageAccessDialog, Topic } from "../components/ManageAccessDialog";
import {
  ResourcesToolbar,
  ResourcesFilterState,
} from "../components/ResourcesToolbar";
import { AiFolderIcon } from "@/v2/assets/icons/AiFolderIcon";

interface SnippetsTabProps {
  releaseId: number | undefined;
  publishedReleaseId?: number;
  simulatorId?: string;
}

type SnippetRow = SnippetResource & {
  isSelected: boolean;
  isStaged: boolean;
  publishedTopicsAccess: ReleaseAccessResourceTopic[];
};

export function SnippetsTab({ releaseId, publishedReleaseId, simulatorId }: SnippetsTabProps) {
  const { data: snippets = [], isLoading } = useGetSnippetsByRelease(releaseId);
  const { data: publishedSnippets = [] } = useGetSnippetsByRelease(publishedReleaseId);
  const { data: topicsData } = useGetTopics();
  const updateSnippetAccess = useUpdateSnippetAccess(releaseId, simulatorId);

  const collections = useMemo(() => {
    const uniquePaths = new Set(
      snippets
        .map((snippet) => snippet.path.split("/").pop())
        .filter((path): path is string => Boolean(path?.trim()))
    );

    return ["No collection", ...Array.from(uniquePaths).sort((a, b) => a.localeCompare(b))];
  }, [snippets]);

  const topics: Topic[] = useMemo(() => {
    if (!topicsData) return [];
    return topicsData.map((t: { id: number; topic: string }) => ({
      id: t.id,
      topic: t.topic,
    }));
  }, [topicsData]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filterState, setFilterState] = useState<ResourcesFilterState>({
    accessLevels: [],
    searchQuery: "",
    categories: [],
  });
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    resourceIds: string[];
    resourceName: string;
    currentStatus?: ReleaseAccessStatus;
    currentTopicsAccess?: ReleaseAccessResourceTopic[];
    publishedTopicsAccess?: ReleaseAccessResourceTopic[];
    currentDefaultTopicAccess?: ReleaseAccessStatus | null;
  }>({
    open: false,
    resourceIds: [],
    resourceName: "",
  });

  const publishedStatusMap = useMemo(() => {
    const map = new Map<string, ReleaseAccessStatus>();
    for (const snippet of publishedSnippets) {
      map.set(snippet.id, snippet.accessStatus);
    }
    return map;
  }, [publishedSnippets]);

  const publishedTopicsAccessMap = useMemo(() => {
    const map = new Map<string, ReleaseAccessResourceTopic[]>();
    for (const snippet of publishedSnippets) {
      map.set(snippet.id, snippet.topicsAccess);
    }
    return map;
  }, [publishedSnippets]);

  const filteredSnippets = useMemo(() => {
    return snippets.filter((snippet) => {
      const matchesSearch =
        filterState.searchQuery === "" ||
        (snippet.title ?? snippet.path)
          .toLowerCase()
          .includes(filterState.searchQuery.toLowerCase());

      const matchesAccess =
        filterState.accessLevels.length === 0 ||
        filterState.accessLevels.includes(snippet.accessStatus);
      
      const matchesCategory =
        filterState.categories.length === 0 ||
        filterState.categories.includes(snippet.path.split("/").pop() || "No collection");

      return matchesSearch && matchesAccess && matchesCategory;
    });
  }, [snippets, filterState]);

  const rows: SnippetRow[] = useMemo(() => {
    return filteredSnippets.map((snippet) => {
      const publishedStatus = publishedStatusMap.get(snippet.id);
      const isStaged =
        publishedReleaseId !== undefined &&
        publishedStatus !== undefined &&
        publishedStatus !== snippet.accessStatus;

      return {
        ...snippet,
        isSelected: selectedIds.has(snippet.id),
        isStaged,
        publishedTopicsAccess: publishedTopicsAccessMap.get(snippet.id) ?? [],
      };
    }).sort((a, b) => a.title?.localeCompare(b.title ?? "") ?? 0);
  }, [filteredSnippets, selectedIds, publishedStatusMap, publishedTopicsAccessMap, publishedReleaseId]);

  const allSelected = useMemo(() => {
    return rows.length > 0 && rows.every((row) => row.isSelected);
  }, [rows]);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((r) => r.id)));
    }
  }, [allSelected, rows]);

  const handleSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleOpenManageAccess = useCallback(
    (
      resourceIds: string[],
      resourceName: string,
      currentStatus?: ReleaseAccessStatus,
      currentTopicsAccess?: ReleaseAccessResourceTopic[],
      publishedTopicsAccess?: ReleaseAccessResourceTopic[],
      currentDefaultTopicAccess?: ReleaseAccessStatus | null
    ) => {
      setDialogState({
        open: true,
        resourceIds,
        resourceName,
        currentStatus,
        currentTopicsAccess,
        publishedTopicsAccess,
        currentDefaultTopicAccess,
      });
    },
    []
  );

  const handleUpdateAccess = useCallback(
    async (
      status: ReleaseAccessStatus,
      topicsAccess?: ReleaseAccessResourceTopic[],
      defaultTopicAccess?: ReleaseAccessStatus | null
    ) => {
      await updateSnippetAccess.mutateAsync({
        snippets: dialogState.resourceIds.map((id) => ({
          id,
          status,
          topicsAccess,
          defaultTopicAccess,
        })),
      });
      setDialogState((prev) => ({ ...prev, open: false }));
      setSelectedIds(new Set());
    },
    [dialogState.resourceIds, updateSnippetAccess]
  );

  const handleBulkAccessClick = useCallback(() => {
    const selectedSnippets = rows.filter((r) => selectedIds.has(r.id));
    if (selectedSnippets.length === 0) return;

    handleOpenManageAccess(
      Array.from(selectedIds),
      `${selectedSnippets.length} snippets`,
      selectedSnippets[0].accessStatus,
      selectedSnippets[0].topicsAccess,
      selectedSnippets[0].publishedTopicsAccess,
      selectedSnippets[0].defaultTopicAccess
    );
  }, [rows, selectedIds, handleOpenManageAccess]);

  const getRowActions = useCallback(
    (row: SnippetRow): RowActions<SnippetRow> => [
      {
        label: "Manage Access",
        icon: <Pencil1Icon />,
        onClick: () =>
          handleOpenManageAccess([row.id], row.title ?? row.path, row.accessStatus, row.topicsAccess, row.publishedTopicsAccess, row.defaultTopicAccess),
      },
    ],
    [handleOpenManageAccess]
  );

  const handleRowClick = useCallback(
    (row: SnippetRow) => {
      handleOpenManageAccess([row.id], row.title ?? row.path, row.accessStatus, row.topicsAccess, row.publishedTopicsAccess, row.defaultTopicAccess);
    },
    [handleOpenManageAccess]
  );

  const columns: ColumnDef<SnippetRow>[] = useMemo(
    () => [
      {
        id: "select",
        header: () => (
          <Checkbox
            checked={allSelected}
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.original.isSelected}
            onCheckedChange={() => handleSelectRow(row.original.id)}
            aria-label={`Select ${row.original.title ?? row.original.path}`}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        meta: {
          className: "w-[40px]",
        },
      },
      {
        header: "Snippet Name",
        accessorKey: "title",
        cell: ({ row }) => (
          <Flex align="center" gap="2">
            <Flex
              align="center"
              justify="center"
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: "var(--orange-3)",
                borderRadius: "4px",
                flexShrink: 0,
              }}
            >
              <AiFolderIcon fill="amber" size={12} />
            </Flex>
            <Text size="2" color="gray" weight="medium" highContrast truncate>
              {row.original.title ?? row.original.path}
            </Text>
          </Flex>
        ),
      },
      {
        header: "Collection",
        id: "collection",
        cell: ({ row }) => (
          <Badge color="gray" size="1" variant="soft">
          {row.original.path.split("/").pop()}
          </Badge>
        ),
        meta: {
          className: "w-[200px]",
        },
      },
      {
        header: "Access",
        accessorKey: "accessStatus",
        cell: ({ row }) => (
          <AccessBadge
            status={row.original.accessStatus}
            isStaged={row.original.isStaged}
            topicsAccess={row.original.topicsAccess}
            publishedTopicsAccess={row.original.publishedTopicsAccess}
            topics={topics}
          />
        ),
        meta: {
          className: "w-[180px]",
          cellTooltip: false,
        },
      },
    ],
    [allSelected, handleSelectAll, handleSelectRow, topics]
  );

  return (
    <Flex direction="column" width="100%">
      <ResourcesToolbar
        title="Snippets"
        count={filteredSnippets.length}
        filterState={filterState}
        onFilterChange={setFilterState}
        selectedCount={selectedIds.size}
        onBulkAccessClick={handleBulkAccessClick}
        categoryFilter={{
          title: "Collection",
          options: collections,
        }}
      />

      {rows.length === 0 && !isLoading && (filterState.searchQuery || filterState.accessLevels.length > 0) ? (
        <Flex align="center" justify="center" py="9">
          <Text size="2" color="gray">
            No results found
          </Text>
        </Flex>
      ) : (
        <SimpleTable
          data={rows}
          columns={columns}
          loading={isLoading}
          getRowActions={getRowActions}
          onRowClicked={handleRowClick}
        />
      )}

      <ManageAccessDialog
        open={dialogState.open}
        onOpenChange={(open) => setDialogState((prev) => ({ ...prev, open }))}
        resourceName={dialogState.resourceName}
        resourceCount={dialogState.resourceIds.length > 1 ? dialogState.resourceIds.length : undefined}
        currentStatus={dialogState.currentStatus}
        currentTopicsAccess={dialogState.currentTopicsAccess}
        publishedTopicsAccess={dialogState.publishedTopicsAccess}
        currentDefaultTopicAccess={dialogState.currentDefaultTopicAccess}
        topics={topics}
        onUpdateAccess={handleUpdateAccess}
        isLoading={updateSnippetAccess.isLoading}
      />
    </Flex>
  );
}
